import React, { useCallback, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';

const HoverInfo = ({ 
  map, 
  currentYear, 
  yearData, 
  findClusterForPoint,
  isEnabled = true,
  isMobile = false // NEW: Accept mobile prop
}) => {
  const [hoverData, setHoverData] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [touchPopup, setTouchPopup] = useState(null); // NEW: For mobile popups

  // ENHANCED: School name normalization (matching MapContainer logic)
  const normalizeSchoolName = useCallback((name, type) => {
    if (!name || typeof name !== 'string') return '';
    
    let normalized = name.trim()
      .replace(/\s+/g, ' ')
      .replace(/\s*(Elementary|ES|E\.S\.)(\s*School)?$/i, '')
      .replace(/\s*(Middle|MS|M\.S\.)(\s*School)?$/i, '')
      .replace(/\s*(High|HS|H\.S\.)(\s*School)?$/i, '')
      .replace(/\s*School$/i, '')
      .trim();

    const specialCases = {
      'B-CC': 'Bethesda-Chevy Chase',
      'BCC': 'Bethesda-Chevy Chase',
      'Bethesda Chevy Chase': 'Bethesda-Chevy Chase',
      'RMS': 'Richard Montgomery',
      'RM': 'Richard Montgomery',
      'WJ': 'Walter Johnson',
      'Blair': 'Montgomery Blair',
      'Einstein': 'Albert Einstein',
      'Wootton': 'Thomas S. Wootton',
      'Churchill': 'Winston Churchill',
      'Whitman': 'Walt Whitman'
    };

    if (specialCases[normalized]) {
      normalized = specialCases[normalized];
    }

    return normalized;
  }, []);

  // ENHANCED: Extract school name with same logic as MapContainer
  const extractSchoolName = useCallback((feature, type) => {
    const properties = feature.properties || {};
    let rawName;
    
    if (type === 'elementary' || type === 'middleSchool') {
      rawName = properties.S_NAME || 
               properties.SCHNAME || 
               properties.NAME ||
               properties.SCHOOLNAME ||
               properties.School_Name ||
               properties.school_name ||
               // 2024-2025 specific fields
               properties.ES_csv_S_N ||  // Elementary school name in 2024-2025
               properties.MS_csv_S_N ||  // Middle school name in 2024-2025
               properties.ES_csv_P_N ||  // Alternative elementary field
               properties.MS_csv_P_N ||  // Alternative middle field
               `${type === 'elementary' ? 'Elementary' : 'Middle'} School`;
    } else { // highSchool
      rawName = properties.CLUSTER || 
               properties.C_NAME || 
               properties.S_NAME || 
               properties.NAME ||
               properties.SCHOOLNAME ||
               properties.School_Name ||
               properties.school_name ||
               properties.SCHNAME ||
               // 2024-2025 specific fields
               properties.HS_csv_S_N ||  // High school name in 2024-2025
               properties.HS_csv_C_N ||  // High school cluster name
               properties.HS_csv_P_N ||  // Alternative high school field
               'High School';
    }
    
    return rawName;
  }, []);

  // ENHANCED: Calculate area with multiple fallback methods
  const calculateArea = useCallback((feature) => {
    const properties = feature.properties || {};
    
    // Try different possible area field names
    const areaFields = [
      'AREA', 'Area', 'area', 
      'SHAPE_Area', 'Shape_Area', 'SHAPE_AREA', 'shape_area',
      'ACRES', 'Acres', 'acres',
      'SQ_FEET', 'SQFEET', 'sq_feet', 'sqfeet',
      'POLYGON_AREA', 'GEOM_AREA', 'GEOMETRY_AREA'
    ];
    
    for (const field of areaFields) {
      if (properties[field] != null && properties[field] !== '' && properties[field] !== 0) {
        const areaValue = typeof properties[field] === 'number' ? 
          properties[field] : 
          parseFloat(String(properties[field]).replace(/[^\d.-]/g, ''));
        
        if (!isNaN(areaValue) && areaValue > 0) {
          // Convert to acres based on likely unit
          let acres;
          if (areaValue > 100000) {
            // Likely square feet
            acres = Math.round(areaValue / 43560);
          } else if (areaValue > 1000) {
            // Could be square meters
            acres = Math.round((areaValue * 10.764) / 43560);
          } else {
            // Likely already in acres
            acres = Math.round(areaValue);
          }
          return `${acres.toLocaleString()} acres`;
        }
      }
    }
    
    // Fallback: calculate from geometry if available
    if (feature.geometry && feature.geometry.coordinates) {
      try {
        const coordinates = feature.geometry.coordinates;
        let totalArea = 0;
        
        const rings = Array.isArray(coordinates[0][0]) ? 
          (typeof coordinates[0][0][0] === 'number' ? [coordinates] : coordinates) : 
          coordinates;

        rings.forEach(polygon => {
          const ring = Array.isArray(polygon[0][0]) ? polygon[0] : polygon;
          
          if (ring.length >= 3) {
            let area = 0;
            for (let i = 0; i < ring.length - 1; i++) {
              const [x1, y1] = ring[i];
              const [x2, y2] = ring[i + 1];
              area += (x1 * y2) - (x2 * y1);
            }
            totalArea += Math.abs(area) / 2;
          }
        });

        if (totalArea > 0) {
          const lat = coordinates[0] && coordinates[0][0] && coordinates[0][0][1] ? coordinates[0][0][1] : 39;
          const metersPerDegreeLat = 111320;
          const metersPerDegreeLng = 111320 * Math.cos(lat * Math.PI / 180);
          
          const areaInSquareMeters = totalArea * metersPerDegreeLat * metersPerDegreeLng;
          const areaInAcres = (areaInSquareMeters * 10.764) / 43560;
          
          return `${Math.round(areaInAcres).toLocaleString()} acres (calculated)`;
        }
      } catch (error) {
        console.warn('Error calculating area from geometry:', error);
      }
    }
    
    return 'N/A';
  }, []);

  // ENHANCED: Get cluster name with year-specific logic
  const getClusterName = useCallback((feature, type, lngLat) => {
    const properties = feature.properties || {};
    let clusterName = 'N/A';
    
    // Try property-based cluster name first
    if (currentYear === '1999') {
      clusterName = properties.CLUSTER || properties.C_NAME || properties.S_NAME || 'N/A';
    } else {
      clusterName = properties.C_NAME || properties.CLUSTER || properties.Cluster || 
                   properties.NAME || properties.CLUSTER_NAME || 'N/A';
    }
    
    // If no cluster name found and we have coordinates, try spatial lookup
    if (clusterName === 'N/A' && lngLat && findClusterForPoint && yearData[currentYear]) {
      const clusterData = yearData[currentYear]?.clusters || 
                         (currentYear === '1999' ? yearData[currentYear]?.highSchool : null);
      if (clusterData) {
        const clusterInfo = findClusterForPoint(lngLat.lng, lngLat.lat, clusterData);
        if (clusterInfo) {
          clusterName = clusterInfo.NAME || clusterInfo.CLUSTER || clusterInfo.C_NAME || 'Found';
        }
      }
    }
    
    return clusterName;
  }, [currentYear, yearData, findClusterForPoint]);

  // ENHANCED: Process hover data with robust property extraction
  const processHoverData = useCallback((feature, lngLat) => {
    const layerId = feature.layer?.id;
    let type, schoolName, clusterName, area;
    
    if (layerId === 'elementary-fill') {
      type = 'elementary';
      schoolName = extractSchoolName(feature, 'elementary');
      clusterName = getClusterName(feature, 'elementary', lngLat);
      area = calculateArea(feature);
    } else if (layerId === 'middleschool-fill') {
      type = 'middleSchool';
      schoolName = extractSchoolName(feature, 'middleSchool');
      clusterName = getClusterName(feature, 'middleSchool', lngLat);
      area = calculateArea(feature);
    } else if (layerId === 'highschool-fill') {
      type = 'highSchool';
      schoolName = extractSchoolName(feature, 'highSchool');
      clusterName = getClusterName(feature, 'highSchool', lngLat);
      area = calculateArea(feature);
    } else {
      return null;
    }
    
    return {
      type,
      schoolName,
      clusterName,
      area,
      year: currentYear,
      coordinates: lngLat
    };
  }, [currentYear, extractSchoolName, getClusterName, calculateArea]);

  // NEW: Create tooltip HTML for mobile popups (using the same styling)
  const createTooltipHTML = useCallback((data) => {
    const getTypeColor = (type) => {
      switch (type) {
        case 'elementary': return '#3B82F6';
        case 'middleSchool': return '#10B981';
        case 'highSchool': return '#EF4444';
        default: return '#6B7280';
      }
    };

    const getTypeLabel = (type) => {
      switch (type) {
        case 'elementary': return 'Elementary School';
        case 'middleSchool': return 'Middle School';
        case 'highSchool': return 'High School';
        default: return 'School';
      }
    };

    const getTypeIcon = (type) => {
      switch (type) {
        case 'elementary':
          return 'M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z';
        case 'highSchool':
          return 'M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.909V17h2V9L12 3z';
        case 'middleSchool':
        default:
          return 'M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z';
      }
    };

    const color = getTypeColor(data.type);

    return `
      <div style="
        background: linear-gradient(135deg, rgba(8, 12, 16, 0.97) 0%, rgba(15, 20, 25, 0.97) 100%);
        border: 1px solid rgba(255, 255, 255, 0.08);
        borderRadius: 12px;
        padding: 0;
        fontFamily: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
        boxShadow: 0 20px 40px -8px rgba(0, 0, 0, 0.6), 0 0 0 0.5px rgba(255, 255, 255, 0.03);
        backdropFilter: blur(24px);
        minWidth: ${isMobile ? '220px' : '240px'};
        maxWidth: ${isMobile ? '260px' : '280px'};
        overflow: hidden;
      ">
        <style>
          .mapboxgl-popup-content {
            padding: 0 !important;
            background: transparent !important;
            boxShadow: none !important;
            borderRadius: 12px !important;
          }
          .mapboxgl-popup-tip {
            borderTopColor: rgba(8, 12, 16, 0.97) !important;
          }
          .mapboxgl-popup-close-button {
            color: rgba(255, 255, 255, 0.6) !important;
            font-size: ${isMobile ? '20px' : '16px'} !important;
            padding: ${isMobile ? '8px' : '4px'} !important;
            width: auto !important;
            height: auto !important;
            border-radius: 6px !important;
            background: rgba(255, 255, 255, 0.1) !important;
            margin: ${isMobile ? '8px 8px 0 0' : '4px 4px 0 0'} !important;
            right: ${isMobile ? '8px' : '4px'} !important;
            top: ${isMobile ? '8px' : '4px'} !important;
          }
          .mapboxgl-popup-close-button:hover {
            background: rgba(255, 255, 255, 0.2) !important;
            color: rgba(255, 255, 255, 0.9) !important;
          }
        </style>
        
        <!-- Header -->
        <div style="
          background: linear-gradient(135deg, ${color}12 0%, transparent 70%);
          borderBottom: 1px solid rgba(255, 255, 255, 0.04);
          padding: ${isMobile ? '12px 14px 8px 14px' : '14px 16px 10px 16px'};
          display: flex;
          alignItems: center;
          gap: 8px;
        ">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="${color}" style="
            opacity: 0.9;
            filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
            flex-shrink: 0;
          ">
            <path d="${getTypeIcon(data.type)}" />
          </svg>
          <h4 style="
            fontWeight: 600;
            fontSize: ${isMobile ? '12px' : '13px'};
            color: ${color};
            margin: 0;
            lineHeight: 1.2;
          ">
            ${getTypeLabel(data.type)}
          </h4>
        </div>
        
        <!-- Content -->
        <div style="padding: ${isMobile ? '10px 14px 12px 14px' : '12px 16px 14px 16px'};">
          <div style="
            display: flex;
            justifyContent: space-between;
            alignItems: flex-start;
            marginBottom: 8px;
            lineHeight: 1.3;
          ">
            <span style="
              color: rgba(255, 255, 255, 0.55);
              fontWeight: 500;
              fontSize: ${isMobile ? '10px' : '11px'};
              textTransform: uppercase;
              letterSpacing: 0.025em;
              minWidth: 50px;
            ">
              School:&nbsp;
            </span>
            <span style="
              color: rgba(255, 255, 255, 0.98);
              fontWeight: 700;
              fontSize: ${isMobile ? '12px' : '13px'};
              textAlign: right;
              maxWidth: ${isMobile ? '140px' : '160px'};
              overflow: hidden;
              textOverflow: ellipsis;
              whiteSpace: nowrap;
            ">
              ${data.schoolName}
            </span>
          </div>
          
          <div style="
            display: flex;
            justifyContent: space-between;
            alignItems: flex-start;
            marginBottom: 8px;
            lineHeight: 1.3;
          ">
            <span style="
              color: rgba(255, 255, 255, 0.55);
              fontWeight: 500;
              fontSize: ${isMobile ? '10px' : '11px'};
              textTransform: uppercase;
              letterSpacing: 0.025em;
              minWidth: 50px;
            ">
              Cluster&nbsp;
            </span>
            <span style="
              color: rgba(255, 255, 255, 0.95);
              fontWeight: 600;
              fontSize: ${isMobile ? '11px' : '12px'};
              textAlign: right;
              maxWidth: ${isMobile ? '140px' : '160px'};
              overflow: hidden;
              textOverflow: ellipsis;
              whiteSpace: nowrap;
            ">
              ${data.clusterName}
            </span>
          </div>
          
          <div style="
            display: flex;
            justifyContent: space-between;
            alignItems: flex-start;
            marginBottom: 8px;
            lineHeight: 1.3;
          ">
            <span style="
              color: rgba(255, 255, 255, 0.55);
              fontWeight: 500;
              fontSize: ${isMobile ? '10px' : '11px'};
              textTransform: uppercase;
              letterSpacing: 0.025em;
              minWidth: 50px;
            ">
              Year:&nbsp;
            </span>
            <span style="
              color: rgba(255, 255, 255, 0.95);
              fontWeight: 600;
              fontSize: ${isMobile ? '11px' : '12px'};
              textAlign: right;
              maxWidth: ${isMobile ? '140px' : '160px'};
            ">
              ${data.year}
            </span>
          </div>
          
          <div style="
            display: flex;
            justifyContent: space-between;
            alignItems: flex-start;
            lineHeight: 1.3;
          ">
            <span style="
              color: rgba(255, 255, 255, 0.55);
              fontWeight: 500;
              fontSize: ${isMobile ? '10px' : '11px'};
              textTransform: uppercase;
              letterSpacing: 0.025em;
              minWidth: 50px;
            ">
              Area:&nbsp;
            </span>
            <span style="
              color: rgba(255, 255, 255, 0.95);
              fontWeight: 600;
              fontSize: ${isMobile ? '11px' : '12px'};
              textAlign: right;
              maxWidth: ${isMobile ? '140px' : '160px'};
            ">
              ${data.area}
            </span>
          </div>
        </div>
      </div>
    `;
  }, [isMobile]);

  // Setup interactions - ENHANCED for both desktop and mobile
  useEffect(() => {
    if (!map || !isEnabled) return;

    let isClosingPopup = false; // Mobile popup close flag

    // DESKTOP: Hover interactions
    const handleMouseMove = (e) => {
      if (isMobile) return; // Skip on mobile
      
      try {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['elementary-fill', 'middleschool-fill', 'highschool-fill']
        });

        if (features && features.length > 0) {
          const feature = features[0];
          const processed = processHoverData(feature, e.lngLat);
          
          if (processed) {
            setHoverData(processed);
            setMousePosition({ x: e.point.x, y: e.point.y });
          }
        } else {
          setHoverData(null);
        }
      } catch (error) {
        console.error('Error in hover mousemove:', error);
        setHoverData(null);
      }
    };

    const handleMouseLeave = () => {
      if (isMobile) return; // Skip on mobile
      setHoverData(null);
    };

    // MOBILE: Touch interactions
    const handleMobileClick = (e) => {
      if (!isMobile) return; // Skip on desktop
      
      if (e.preventDefault) e.preventDefault();
      
      if (isClosingPopup) {
        return;
      }
      
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['elementary-fill', 'middleschool-fill', 'highschool-fill']
      });
      
      if (features.length === 0) {
        if (touchPopup) {
          touchPopup.remove();
          setTouchPopup(null);
        }
        return;
      }
      
      // Close existing popup
      if (touchPopup) {
        touchPopup.remove();
        setTouchPopup(null);
      }
      
      // Select best feature (priority: Elementary > Middle > High)
      let selectedFeature = features[0];
      for (const feature of features) {
        if (feature.layer.id === 'elementary-fill') {
          selectedFeature = feature;
          break;
        } else if (feature.layer.id === 'middleschool-fill') {
          selectedFeature = feature;
        }
      }
      
      const processed = processHoverData(selectedFeature, e.lngLat);
      if (!processed) return;
      
      // Create mobile popup with HoverInfo styling
      const popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        className: 'premium-tooltip mobile-tooltip',
        maxWidth: '280px',
        anchor: 'bottom'
      })
        .setLngLat(e.lngLat)
        .setHTML(createTooltipHTML(processed))
        .addTo(map);

      setTouchPopup(popup);

      // Handle popup close
      popup.on('close', () => {
        isClosingPopup = true;
        setTouchPopup(null);
        setTimeout(() => {
          isClosingPopup = false;
        }, 300);
      });

      // Auto-close after 15 seconds
      const autoCloseTimer = setTimeout(() => {
        if (popup && popup.isOpen()) {
          popup.remove();
          setTouchPopup(null);
        }
      }, 15000);

      popup.on('close', () => {
        clearTimeout(autoCloseTimer);
      });
    };

    // Add appropriate event listeners based on device type
    if (isMobile) {
      map.on('click', handleMobileClick);
    } else {
      map.on('mousemove', handleMouseMove);
      map.on('mouseleave', 'elementary-fill', handleMouseLeave);
      map.on('mouseleave', 'middleschool-fill', handleMouseLeave);
      map.on('mouseleave', 'highschool-fill', handleMouseLeave);
    }

    // Cursor changes for both desktop and mobile
    ['elementary-fill', 'middleschool-fill', 'highschool-fill'].forEach(layer => {
      map.on('mouseenter', layer, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layer, () => {
        map.getCanvas().style.cursor = '';
      });
    });

    return () => {
      try {
        if (isMobile) {
          map.off('click', handleMobileClick);
        } else {
          map.off('mousemove', handleMouseMove);
          map.off('mouseleave', 'elementary-fill', handleMouseLeave);
          map.off('mouseleave', 'middleschool-fill', handleMouseLeave);
          map.off('mouseleave', 'highschool-fill', handleMouseLeave);
        }
        
        ['elementary-fill', 'middleschool-fill', 'highschool-fill'].forEach(layer => {
          map.off('mouseenter', layer);
          map.off('mouseleave', layer);
        });
        
        // Clean up mobile popup if it exists
        if (touchPopup) {
          touchPopup.remove();
          setTouchPopup(null);
        }
      } catch (error) {
        console.warn('Error cleaning up hover listeners:', error);
      }
    };
  }, [map, isEnabled, isMobile, processHoverData, createTooltipHTML, touchPopup]);

  // DESKTOP: Render hover tooltip (fixed position)
  if (!isMobile && hoverData) {
    const getTypeColor = (type) => {
      switch (type) {
        case 'elementary': return '#3B82F6';
        case 'middleSchool': return '#10B981';
        case 'highSchool': return '#EF4444';
        default: return '#6B7280';
      }
    };

    const getTypeLabel = (type) => {
      switch (type) {
        case 'elementary': return 'Elementary School';
        case 'middleSchool': return 'Middle School';
        case 'highSchool': return 'High School';
        default: return 'School';
      }
    };

    const getTypeIcon = (type) => {
      switch (type) {
        case 'elementary':
          return 'M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z';
        case 'highSchool':
          return 'M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.909V17h2V9L12 3z';
        case 'middleSchool':
        default:
          return 'M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z';
      }
    };

    const color = getTypeColor(hoverData.type);

    return (
      <div
        style={{
          position: 'fixed',
          left: mousePosition.x + 15,
          top: mousePosition.y - 10,
          transform: 'translateY(-100%)',
          background: 'linear-gradient(135deg, rgba(8, 12, 16, 0.97) 0%, rgba(15, 20, 25, 0.97) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          padding: '0',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif',
          boxShadow: '0 20px 40px -8px rgba(0, 0, 0, 0.6), 0 0 0 0.5px rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(24px)',
          minWidth: '240px',
          maxWidth: '280px',
          overflow: 'hidden',
          zIndex: 1000,
          pointerEvents: 'none',
          animation: 'tooltipFadeIn 0.15s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        <style>
          {`
            @keyframes tooltipFadeIn {
              from {
                opacity: 0;
                transform: translateY(-100%) translateY(4px) scale(0.96);
              }
              to {
                opacity: 1;
                transform: translateY(-100%) scale(1);
              }
            }
          `}
        </style>
        
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${color}12 0%, transparent 70%)`,
          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
          padding: '14px 16px 10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill={color} style={{
            opacity: 0.9,
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))',
            flexShrink: 0
          }}>
            <path d={getTypeIcon(hoverData.type)} />
          </svg>
          <h4 style={{
            fontWeight: 600,
            fontSize: '13px',
            color: color,
            margin: 0,
            lineHeight: 1.2
          }}>
            {getTypeLabel(hoverData.type)}
          </h4>
        </div>
        
        {/* Content */}
        <div style={{ padding: '12px 16px 14px 16px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '8px',
            lineHeight: 1.3
          }}>
            <span style={{
              color: 'rgba(255, 255, 255, 0.55)',
              fontWeight: 500,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              minWidth: '50px'
            }}>
              School: 
            </span>
            <span style={{
              color: 'rgba(255, 255, 255, 0.98)',
              fontWeight: 700,
              fontSize: '13px',
              textAlign: 'right',
              maxWidth: '160px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {hoverData.schoolName}
            </span>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '8px',
            lineHeight: 1.3
          }}>
            <span style={{
              color: 'rgba(255, 255, 255, 0.55)',
              fontWeight: 500,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              minWidth: '50px'
            }}>
              Cluster: 
            </span>
            <span style={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 600,
              fontSize: '12px',
              textAlign: 'right',
              maxWidth: '160px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {hoverData.clusterName}
            </span>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '8px',
            lineHeight: 1.3
          }}>
            <span style={{
              color: 'rgba(255, 255, 255, 0.55)',
              fontWeight: 500,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              minWidth: '50px'
            }}>
              Year: 
            </span>
            <span style={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 600,
              fontSize: '12px',
              textAlign: 'right',
              maxWidth: '160px'
            }}>
              {hoverData.year}
            </span>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            lineHeight: 1.3
          }}>
            <span style={{
              color: 'rgba(255, 255, 255, 0.55)',
              fontWeight: 500,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              minWidth: '50px'
            }}>
              Area: 
            </span>
            <span style={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 600,
              fontSize: '12px',
              textAlign: 'right',
              maxWidth: '160px'
            }}>
              {hoverData.area}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // MOBILE: No fixed tooltip rendering (popups handle it)
  return null;
};

export default HoverInfo;